import { useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { Queries } from '@shared/api';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';
import { useCurrentBusinessId } from './useCurrentBusinessId';

/**
 * A hook that uses a cache-only policy to retrieve channel list items.
 */
export function useChannelsFromCache(): API.ChannelListItemFragment[] {
  const businessId = useCurrentBusinessId();
  const { data } = useQuery<API.ChannelListQuery>(Queries.channel.list, {
    skip: isNil(businessId),
    fetchPolicy: 'cache-only',
  });

  return useMemo(
    () => (data?.listChannels?.filter(notNil) ?? []) as API.ChannelListItemFragment[],
    [data?.listChannels],
  );
}

interface UseChannelFromCacheProps {
  channelSlug?: string;
  channelId?: number;
}

export function useChannelFromCache({
  channelSlug,
  channelId,
}: UseChannelFromCacheProps): API.ChannelListItemFragment | undefined {
  const channels = useChannelsFromCache();
  if (channelSlug) {
    return channels.find(channel => channel.slug === channelSlug);
  }
  if (channelId) {
    return channels.find(channel => channel.id === channelId);
  }
  return undefined;
}
